// Backend serves routes under /api (e.g. /api/referrals). Use full backend URL + /api in production.
// Set VITE_API_URL for a custom backend. In production we never use localhost.
const productionBackend = 'https://homeland-web-backend.vercel.app';
const envApi = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const baseFromEnv = envApi || (import.meta.env.PROD ? productionBackend : '');
const base = import.meta.env.PROD && (baseFromEnv.includes('localhost') || !baseFromEnv)
  ? productionBackend
  : baseFromEnv;
export const apiBase = base ? (base.endsWith('/api') ? base : `${base}/api`) : '/api';

/** URL to start Google sign-in (redirect to backend). */
export const authLoginUrl =
  (typeof window !== 'undefined' && !apiBase.startsWith('http'))
    ? `${window.location.origin}${apiBase}/auth/google`
    : `${apiBase}/auth/google`;

export type UserRole = 'admin' | 'tutor' | 'student';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  name?: string;
  picture_url?: string;
}

export async function getAuthMe(): Promise<AuthUser | null> {
  const res = await fetch(`${apiBase}/auth/me`, { credentials: 'include' });
  if (res.status === 401) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Auth check failed');
  return data as AuthUser;
}

export interface UpdateProfilePayload {
  name?: string;
  picture_url?: string;
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<void> {
  const res = await fetch(`${apiBase}/auth/me`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update profile');
}

export async function uploadProfilePicture(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${apiBase}/upload/profile-picture`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to upload photo');
  return data as { url: string };
}

/** Home page config (public). */
export interface ConnectSocialLink {
  id: string;
  label: string;
  url: string;
}
export type LogoAlign = 'left' | 'center';

export interface HomeConfig {
  title: string;
  tagline: string;
  heroVideoUrl: string | null;
  headerLogoUrl: string | null;
  headerLogoWidth: string | null;
  headerLogoHeight: string | null;
  headerLogoAlign: string;
  footerLogoUrl: string | null;
  footerLogoWidth: string | null;
  footerLogoHeight: string | null;
  footerLogoAlign: string;
  faviconUrl: string | null;
  faviconWidth: string | null;
  faviconHeight: string | null;
  connectEmail: string | null;
  connectPhone: string | null;
  connectAddress: string | null;
  connectEmailNairobi: string | null;
  connectPhoneNairobi: string | null;
  connectAddressNairobi: string | null;
  connectWhatsappUrl: string | null;
  connectSocialLinks: ConnectSocialLink[];
  // Extended marketing / ecosystem config
  featuredPrograms?: any[];
  ecosystemStats?: any[];
  aboutMission?: string | null;
  aboutVision?: string | null;
  aboutHeroHeading?: string | null;
  aboutHeroSubtext?: string | null;
  aboutHeroParagraph?: string | null;
  aboutMissionImage?: string | null;
  aboutBuildImage?: string | null;
  aboutWhoImage?: string | null;
  aboutHeroImages?: any[];
  aboutValuePillars?: any[];
  aboutCoreValues?: any[];
  aboutBuildAreas?: any[];
  aboutWhoFor?: any[];
  aboutTractionStats?: any[];
  aboutLogoCarousel?: { title?: string | null; items?: Array<{ url: string; name?: string }> } | null;
  aboutEastAfricaCityImages?: Array<{ city?: string; image?: string }>;
  ecosystemHubs?: any[];
  ecosystemPartners?: any[];
  ecosystemEvents?: any[];
}
export async function getHomeConfig(): Promise<HomeConfig> {
  const res = await fetch(`${apiBase}/home`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load home config');
  return data as HomeConfig;
}

/** Admin: public page assets (home hero video, header/footer logos with size, connect section). */
export interface PublicAssets {
  heroVideoUrl: string;
  headerLogoUrl: string;
  headerLogoWidth: string;
  headerLogoHeight: string;
  headerLogoAlign: string;
  footerLogoUrl: string;
  footerLogoWidth: string;
  footerLogoHeight: string;
  footerLogoAlign: string;
  faviconUrl: string;
  faviconWidth: string;
  faviconHeight: string;
  connectEmail: string;
  connectPhone: string;
  connectAddress: string;
  connectEmailNairobi: string;
  connectPhoneNairobi: string;
  connectAddressNairobi: string;
  connectWhatsappUrl: string;
  connectSocialLinks: ConnectSocialLink[];
  // Extended marketing / ecosystem config; kept loose so admin UI can shape them.
  featuredPrograms?: any[];
  ecosystemStats?: any[];
  aboutMission?: string;
  aboutVision?: string;
  aboutHeroHeading?: string;
  aboutHeroSubtext?: string;
  aboutHeroParagraph?: string;
  aboutMissionImage?: string;
  aboutBuildImage?: string;
  aboutWhoImage?: string;
  aboutHeroImages?: any[];
  aboutValuePillars?: any[];
  aboutCoreValues?: any[];
  aboutBuildAreas?: any[];
  aboutWhoFor?: any[];
  aboutTractionStats?: any[];
  aboutLogoCarousel?: { title?: string; items?: Array<{ url: string; name?: string }> };
  aboutEastAfricaCityImages?: Array<{ city?: string; image?: string }>;
  ecosystemHubs?: any[];
  ecosystemPartners?: any[];
  ecosystemEvents?: any[];
}
export async function getPublicAssets(): Promise<PublicAssets> {
  const res = await fetch(`${apiBase}/admin/public-assets`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load public assets');
  return data as PublicAssets;
}
export async function updatePublicAssets(payload: Partial<PublicAssets>): Promise<PublicAssets> {
  const res = await fetch(`${apiBase}/admin/public-assets`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update public assets');
  return data as PublicAssets;
}

// --- Enrollment payments (PayHero) ---

export interface EnrollWithPayHeroPayload {
  courseId: string;
  email: string;
  phone: string;
  fullName: string;
}

export interface EnrollWithPayHeroResult {
  success: boolean;
  /** Set when PayHero STK push was sent; user must complete payment on phone. */
  pending?: boolean;
  reference?: string;
  checkoutRequestID?: string;
  message?: string;
  enrollmentId?: string;
  status?: string;
  enrolledAt?: string;
}

export async function enrollWithPayHero(
  payload: EnrollWithPayHeroPayload
): Promise<EnrollWithPayHeroResult> {
  const res = await fetch(`${apiBase}/payments/payhero/enroll`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { message?: string }).message ?? (data as { error?: string }).error ?? 'Failed to enroll with payment');
  }
  return data as EnrollWithPayHeroResult;
}

export async function authLogout(): Promise<void> {
  await fetch(`${apiBase}/auth/logout`, { method: 'POST', credentials: 'include' });
}

export interface CreateReferralPayload {
  referrerName: string;
  referrerEmail: string;
  referrerPhone?: string;
  describesYou?: string;
  describesYouOther?: string;
  aiKnowledge?: string;
  interests?: string[];
  wantToGain?: string;
  wouldLikeTo?: string;
  contacts: Array<{ name: string; email?: string; phone?: string }>;
}

export interface CreateReferralResponse {
  success: boolean;
  id: string;
  message: string;
}

export async function createReferral(
  payload: CreateReferralPayload
): Promise<CreateReferralResponse> {
  const res = await fetch(`${apiBase}/referrals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to submit referral');
  }
  return data as CreateReferralResponse;
}

export interface CheckReferralReferrerResponse {
  exists: boolean;
}

export async function checkReferralReferrer(email: string): Promise<CheckReferralReferrerResponse> {
  const res = await fetch(`${apiBase}/referrals/check-referrer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to check email');
  }
  return data as CheckReferralReferrerResponse;
}

export interface CheckReferralContactResponse {
  exists: boolean;
}

export async function checkReferralContact(opts: {
  email?: string;
  phone?: string;
}): Promise<CheckReferralContactResponse> {
  const res = await fetch(`${apiBase}/referrals/check-contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: opts.email, phone: opts.phone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to check contact');
  }
  return data as CheckReferralContactResponse;
}

export interface AcceptInvitationResponse {
  accepted: boolean;
  message?: string;
  error?: string;
}

export async function acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
  const res = await fetch(`${apiBase}/invite/accept/${encodeURIComponent(token)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to accept invitation');
  }
  return data as AcceptInvitationResponse;
}

export interface AdminContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  acceptedAt: string | null;
  invitationSentAt: string | null;
  accepted: 'accepted' | 'pending';
}

export interface AdminReferral {
  id: string;
  referrerName: string;
  referrerEmail: string;
  referrerPhone: string;
  createdAt: string;
  contacts: AdminContact[];
}

export interface GetAdminReferralsResponse {
  referrals: AdminReferral[];
}

export async function getAdminReferrals(): Promise<GetAdminReferralsResponse> {
  const res = await fetch(`${apiBase}/admin/referrals`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to load referrals');
  }
  return data as GetAdminReferralsResponse;
}

export interface SendAdminRemindersResponse {
  attempted: number;
  sent: number;
  days: number;
}

export async function sendAdminReminders(days?: number): Promise<SendAdminRemindersResponse> {
  const res = await fetch(`${apiBase}/admin/send-reminders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(days != null ? { days } : {}),
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Failed to send reminders');
  }
  return data as SendAdminRemindersResponse;
}

// --- Admin: Students (enrollments) ---
export interface AdminEnrollment {
  id: string;
  learnerId: string;
  learnerName: string;
  learnerEmail: string;
  learnerPhone: string;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  programTitle: string;
  programSlug: string;
  status: string;
  enrolledAt: string;
  isNew: boolean;
}
export interface GetAdminEnrollmentsResponse {
  newCount: number;
  enrollments: AdminEnrollment[];
}
export async function getAdminEnrollments(): Promise<GetAdminEnrollmentsResponse> {
  const res = await fetch(`${apiBase}/admin/enrollments`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load enrollments');
  return data as GetAdminEnrollmentsResponse;
}
export async function markAdminEnrollmentsSeen(): Promise<void> {
  const res = await fetch(`${apiBase}/admin/enrollments/seen`, {
    method: 'PATCH',
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to mark seen');
}

// --- Admin: Tutors ---
export interface AdminTutorAssignedCourse {
  courseId: string;
  courseTitle: string;
  programTitle: string;
}
export interface AdminTutorAssignedProgram {
  programId: string;
  programTitle: string;
}
export interface AdminTutor {
  id: string;
  email: string;
  name: string;
  pictureUrl: string | null;
  role: string;
  createdAt: string;
  assignedCourses?: AdminTutorAssignedCourse[];
  assignedPrograms?: AdminTutorAssignedProgram[];
}
export interface AdminPendingInvite {
  id: string;
  email: string;
  phone: string;
  courseId: string;
  courseTitle: string;
  courseSlug: string;
  programSlug: string;
  createdAt: string;
}

export interface AdminCourseForAssign {
  courseId: string;
  courseSlug: string;
  courseTitle: string;
  programId: string;
  programSlug: string;
  programTitle: string;
}
export async function getAdminCoursesForAssign(): Promise<{ courses: AdminCourseForAssign[] }> {
  const res = await fetch(`${apiBase}/admin/courses-for-assign`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load courses');
  return data as { courses: AdminCourseForAssign[] };
}
export interface GetAdminTutorsResponse {
  tutors: AdminTutor[];
  pendingInvites: AdminPendingInvite[];
}
export async function getAdminTutors(): Promise<GetAdminTutorsResponse> {
  const res = await fetch(`${apiBase}/admin/tutors`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load tutors');
  return data as GetAdminTutorsResponse;
}
export async function validateTutorInviteToken(token: string): Promise<{ valid: boolean }> {
  const res = await fetch(`${apiBase}/invite/tutor-validate?token=${encodeURIComponent(token)}`);
  const data = await res.json().catch(() => ({ valid: false }));
  return data as { valid: boolean };
}

export async function deleteTutorInvite(inviteId: string): Promise<void> {
  const res = await fetch(`${apiBase}/admin/tutors/invites/${encodeURIComponent(inviteId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message ?? (data as { error?: string }).error ?? 'Failed to cancel invite');
}

export async function removeTutor(tutorId: string): Promise<void> {
  const res = await fetch(`${apiBase}/admin/tutors/${encodeURIComponent(tutorId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message ?? (data as { error?: string }).error ?? 'Failed to remove tutor');
}

export async function inviteTutor(
  email: string,
  options?: { phone?: string; courseId?: string; permissions?: { fullAccess?: boolean } }
): Promise<{ ok: boolean; sent: boolean; message: string }> {
  const res = await fetch(`${apiBase}/admin/tutors/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      phone: options?.phone?.trim() || undefined,
      courseId: options?.courseId || undefined,
      permissions: options?.permissions ?? { fullAccess: true },
    }),
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to send invite');
  return data as { ok: boolean; sent: boolean; message: string };
}

// --- Tutor: assigned courses and programs (for dashboard) ---
export interface TutorAssignedCourse {
  courseId: string;
  courseSlug: string;
  courseTitle: string;
  programId: string;
  programSlug: string;
  programTitle: string;
  assignmentType: 'course';
}
export interface TutorAssignedProgram {
  programId: string;
  programSlug: string;
  programTitle: string;
  assignmentType: 'program';
}
export async function getTutorAssignedCourses(): Promise<{ courses: TutorAssignedCourse[]; programs: TutorAssignedProgram[] }> {
  const res = await fetch(`${apiBase}/tutor/assigned-courses`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load assigned courses');
  return data as { courses: TutorAssignedCourse[]; programs: TutorAssignedProgram[] };
}

export interface TutorStudent {
  enrollmentId: string;
  learnerId: string;
  learnerName: string;
  learnerEmail: string;
  learnerPhone: string;
  courseId: string;
  courseTitle: string;
  programTitle: string;
  status: string;
  enrolledAt: string;
  completedCount: number;
  totalLessons: number;
}
export async function getTutorStudents(): Promise<{ students: TutorStudent[] }> {
  const res = await fetch(`${apiBase}/tutor/students`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load students');
  return data as { students: TutorStudent[] };
}

export interface TutorEnrollmentDetail {
  enrollmentId: string;
  learnerId: string;
  learnerName: string;
  learnerEmail: string;
  learnerPhone: string;
  courseId: string;
  courseTitle: string;
  programTitle: string;
  status: string;
  enrolledAt: string;
  startedAt: string | null;
  completedAt: string | null;
  progress: { completedCount: number; totalLessons: number; percent: number };
}
export async function getTutorEnrollment(enrollmentId: string): Promise<TutorEnrollmentDetail> {
  const res = await fetch(`${apiBase}/tutor/enrollments/${encodeURIComponent(enrollmentId)}`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load enrollment');
  return data as TutorEnrollmentDetail;
}

export interface AdminProgramForAssign {
  programId: string;
  programSlug: string;
  programTitle: string;
}
export async function getAdminProgramsForAssign(): Promise<{ programs: AdminProgramForAssign[] }> {
  const res = await fetch(`${apiBase}/admin/programs-for-assign`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load programs');
  return data as { programs: AdminProgramForAssign[] };
}

export async function updateTutorAssignments(tutorId: string, courseIds: string[], programIds: string[]): Promise<void> {
  const res = await fetch(`${apiBase}/admin/tutors/${encodeURIComponent(tutorId)}/assignments`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ courseIds, programIds }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { message?: string }).message ?? (data as { error?: string }).error ?? 'Failed to update assignments');
}

// Programs & courses (from DB)
export interface ProgramCategory {
  id: string;
  slug: string;
  name: string;
  sort_order: number;
}

export interface Program {
  id: string;
  category_id: string;
  slug: string;
  title: string;
  sort_order: number;
  category_slug?: string;
  category_name?: string;
  course_count?: string;
}

export interface Course {
  id: string;
  program_id: string;
  slug: string;
  title: string;
  duration: string;
  price_kes: number;
  sort_order: number;
  /** CMS: from API when set */
  card_overview?: string | null;
  details?: string | null;
  key_learning_areas?: string[] | null;
  /** Optional image URL for course card on program page */
  image_url?: string | null;
}

export interface ProgramWithCourses extends Program {
  courses: Course[];
}

export interface CourseWithProgram extends Course {
  program_title: string;
  program_slug: string;
  category_name: string;
}

/** Lesson in curriculum (returned when ?curriculum=1) */
export interface CourseLesson {
  id: string;
  sectionId: string;
  title: string;
  slug: string;
  // contentBlocks are fetched via dedicated lesson endpoint
  contentType: string;
  resourceUrl: string | null;
  durationMinutes: number | null;
  sortOrder: number;
  xpPerLesson: number;
}

/** Section (module) in curriculum */
export interface CourseSection {
  id: string;
  courseId: string;
  title: string;
  sortOrder: number;
  description: string | null;
  icon?: string | null;
  totalDuration?: string | null;
  lessons: CourseLesson[];
}

export interface CourseWithCurriculum extends CourseWithProgram {
  sections: CourseSection[];
}

/** Resolve course with full curriculum (sections + lessons). Use for course detail page modules grid. */
export async function getCourseWithCurriculum(programSlugOrId: string, courseSlugOrId: string): Promise<CourseWithCurriculum> {
  const res = await fetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}?curriculum=1`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load course');
  return data as CourseWithCurriculum;
}

export async function getProgramCategories(): Promise<ProgramCategory[]> {
  const res = await fetch(`${apiBase}/courses/categories`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load categories');
  return data as ProgramCategory[];
}

export async function getPrograms(categorySlug?: string): Promise<Program[]> {
  const url = categorySlug ? `${apiBase}/courses?category=${encodeURIComponent(categorySlug)}` : `${apiBase}/courses`;
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load programs');
  return data as Program[];
}

/** Resolve program by slug or UUID. */
export async function getProgram(programSlugOrId: string): Promise<ProgramWithCourses> {
  const res = await fetch(`${apiBase}/courses/${encodeURIComponent(programSlugOrId)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load program');
  return data as ProgramWithCourses;
}

/** Resolve course by program slug/id and course slug/id. Add ?curriculum=1 via getCourseWithCurriculum() for sections/lessons. */
export async function getCourse(programSlugOrId: string, courseSlugOrId: string, options?: { curriculum?: boolean }): Promise<CourseWithProgram | CourseWithCurriculum> {
  const url = `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}${options?.curriculum ? '?curriculum=1' : ''}`;
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load course');
  return data as CourseWithProgram | CourseWithCurriculum;
}

// --- Admin CMS: programs and courses CRUD (require admin auth; use credentials: 'include') ---

const adminFetch = (url: string, opts: RequestInit = {}) =>
  fetch(url, { ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...opts.headers } });

/** Upload image or video for course lesson blocks. Admin or assigned tutor. Image max 5 MB, video max 50 MB. Returns public URL. */
export async function uploadCourseMedia(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${apiBase}/upload/course-media`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Upload failed');
  return data as { url: string };
}

export interface CreateProgramPayload {
  categoryId: string;
  slug: string;
  title: string;
  sortOrder?: number;
}

export interface UpdateProgramPayload {
  categoryId?: string;
  slug?: string;
  title?: string;
  sortOrder?: number;
}

export interface CreateCoursePayload {
  slug: string;
  title: string;
  duration?: string;
  priceKes?: number;
  sortOrder?: number;
  cardOverview?: string;
  details?: string;
  keyLearningAreas?: string[];
  imageUrl?: string;
}

export interface UpdateCoursePayload {
  slug?: string;
  title?: string;
  duration?: string;
  priceKes?: number;
  sortOrder?: number;
  cardOverview?: string;
  details?: string;
  keyLearningAreas?: string[];
  imageUrl?: string;
}

export async function createProgram(payload: CreateProgramPayload): Promise<Program> {
  const res = await adminFetch(`${apiBase}/courses`, { method: 'POST', body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to create program');
  return data as Program;
}

export async function updateProgram(programSlugOrId: string, payload: UpdateProgramPayload): Promise<Program> {
  const res = await adminFetch(`${apiBase}/courses/${encodeURIComponent(programSlugOrId)}`, { method: 'PATCH', body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update program');
  return data as Program;
}

export async function deleteProgram(programSlugOrId: string): Promise<void> {
  const res = await adminFetch(`${apiBase}/courses/${encodeURIComponent(programSlugOrId)}`, { method: 'DELETE' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message ?? data.error ?? 'Failed to delete program');
  }
}

export async function createCourse(programSlugOrId: string, payload: CreateCoursePayload): Promise<Course> {
  const res = await adminFetch(`${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses`, { method: 'POST', body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to create course');
  return data as Course;
}

export async function updateCourse(programSlugOrId: string, courseSlugOrId: string, payload: UpdateCoursePayload): Promise<Course> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update course');
  return data as Course;
}

export async function deleteCourse(programSlugOrId: string, courseSlugOrId: string): Promise<void> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message ?? data.error ?? 'Failed to delete course');
  }
}

// --- Admin: course sections (modules) CRUD ---

export interface CreateSectionPayload {
  title: string;
  sortOrder?: number;
  description?: string;
  icon?: string;
  totalDuration?: string;
  /** When creating a module, create this many placeholder lessons (new, editable in lesson view). */
  lessonCount?: number;
}

export interface UpdateSectionPayload {
  title?: string;
  sortOrder?: number;
  description?: string;
  icon?: string;
  totalDuration?: string;
}

export async function createSection(programSlugOrId: string, courseSlugOrId: string, payload: CreateSectionPayload): Promise<CourseSection> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}/sections`,
    { method: 'POST', body: JSON.stringify(payload) }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to create section');
  return data as CourseSection;
}

export async function updateSection(programSlugOrId: string, courseSlugOrId: string, sectionId: string, payload: UpdateSectionPayload): Promise<CourseSection> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}/sections/${encodeURIComponent(sectionId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update section');
  return data as CourseSection;
}

export async function deleteSection(programSlugOrId: string, courseSlugOrId: string, sectionId: string): Promise<void> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(courseSlugOrId)}/sections/${encodeURIComponent(sectionId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message ?? data.error ?? 'Failed to delete section');
  }
}

// --- Lessons CRUD (blocks-based content) ---

import type { LessonBlock } from './lessonBlocks';

export interface LessonDetail {
  id: string;
  sectionId: string;
  title: string;
  slug: string;
  contentBlocks: LessonBlock[];
  contentType: string;
  resourceUrl: string | null;
  durationMinutes: number | null;
  sortOrder: number;
  xpPerLesson: number;
}

export interface UpdateLessonPayload {
  title?: string;
  slug?: string;
  contentBlocks?: LessonBlock[];
  contentType?: string;
  resourceUrl?: string | null;
  durationMinutes?: number | null;
  sortOrder?: number;
  xpPerLesson?: number;
}

export async function getLesson(
  programSlugOrId: string,
  courseSlugOrId: string,
  sectionId: string,
  lessonId: string
): Promise<LessonDetail> {
  const url = `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(
    courseSlugOrId
  )}/sections/${encodeURIComponent(sectionId)}/lessons/${encodeURIComponent(lessonId)}`;
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load lesson');
  return data as LessonDetail;
}

export async function updateLesson(
  programSlugOrId: string,
  courseSlugOrId: string,
  sectionId: string,
  lessonId: string,
  payload: UpdateLessonPayload
): Promise<LessonDetail> {
  const res = await adminFetch(
    `${apiBase}/courses/${encodeURIComponent(programSlugOrId)}/courses/${encodeURIComponent(
      courseSlugOrId
    )}/sections/${encodeURIComponent(sectionId)}/lessons/${encodeURIComponent(lessonId)}`,
    { method: 'PATCH', body: JSON.stringify(payload) }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update lesson');
  return data as LessonDetail;
}

// --- LMS: enrollment, progress, XP (see docs/lms-data-model.md) ---

export interface LmsEnrollmentSummary {
  id: string;
  courseId: string;
  status: string;
  enrolledAt: string;
  startedAt: string | null;
  completedAt: string | null;
  course: { title: string; slug: string; programTitle: string; programSlug: string };
}

export interface LmsEnrollmentStatus {
  enrolled: boolean;
  enrollment: { id: string; status: string; enrolledAt: string } | null;
}

export interface LmsProgress {
  enrollmentId: string;
  completedLessonIds: string[];
  completedCount: number;
  totalLessons: number;
  percent: number;
}

export interface LmsLearnerXp {
  learnerId: string;
  totalXp: number;
  updatedAt: string | null;
}

/** Get or create learner for current user (requires auth). Admin has no learner record; returns learnerId: null. */
export async function lmsGetMeLearner(): Promise<{ learnerId: string | null }> {
  const res = await fetch(`${apiBase}/lms/me/learner`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to get learner');
  return data as { learnerId: string | null };
}

export async function lmsGetEnrollments(learnerId: string): Promise<{ enrollments: LmsEnrollmentSummary[] }> {
  const res = await fetch(`${apiBase}/lms/learners/${encodeURIComponent(learnerId)}/enrollments`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load enrollments');
  return data as { enrollments: LmsEnrollmentSummary[] };
}

export async function lmsGetEnrollmentStatus(learnerId: string, courseId: string): Promise<LmsEnrollmentStatus> {
  const res = await fetch(`${apiBase}/lms/learners/${encodeURIComponent(learnerId)}/enrollment-status?courseId=${encodeURIComponent(courseId)}`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to check enrollment');
  return data as LmsEnrollmentStatus;
}

export async function lmsEnroll(learnerId: string, courseId: string): Promise<{ id: string; status: string; enrolledAt: string }> {
  const res = await fetch(`${apiBase}/lms/enrollments`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ learnerId, courseId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to enroll');
  return data as { id: string; status: string; enrolledAt: string };
}

export async function lmsGetProgress(enrollmentId: string): Promise<LmsProgress> {
  const res = await fetch(`${apiBase}/lms/enrollments/${encodeURIComponent(enrollmentId)}/progress`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load progress');
  return data as LmsProgress;
}

export async function lmsCompleteLesson(
  enrollmentId: string,
  lessonId: string,
  options?: { quizPassed?: boolean }
): Promise<{ completed: boolean; xpAwarded: number; totalXp: number }> {
  const res = await fetch(`${apiBase}/lms/enrollments/${encodeURIComponent(enrollmentId)}/lessons/${encodeURIComponent(lessonId)}/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(options?.quizPassed !== undefined ? { quizPassed: options.quizPassed } : {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to record completion');
  return data as { completed: boolean; xpAwarded: number; totalXp: number };
}

export async function lmsGetLearnerXp(learnerId: string): Promise<LmsLearnerXp> {
  const res = await fetch(`${apiBase}/lms/learners/${encodeURIComponent(learnerId)}/xp`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load XP');
  return data as LmsLearnerXp;
}

// --- LMS: homework (student) ---

export interface LmsHomeworkAssignment {
  id: string;
  courseId: string;
  courseTitle: string;
  title: string;
  description: string;
  type: string;
  dueDate: string | null;
  maxScore: number;
  xpReward: number;
  instructions: string;
  status: 'pending' | 'submitted' | 'graded' | 'late';
  submittedAt: string | null;
  score: number | null;
}

export async function lmsGetMyHomework(): Promise<{ assignments: LmsHomeworkAssignment[] }> {
  const res = await fetch(`${apiBase}/lms/me/homework`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load homework');
  return data as { assignments: LmsHomeworkAssignment[] };
}

export async function uploadHomeworkAttachment(file: File): Promise<{ url: string; name: string }> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${apiBase}/upload/homework`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to upload attachment');
  return data as { url: string; name: string };
}

export async function lmsSubmitHomework(
  assignmentId: string,
  payload: { content: string; fileNames?: string[] }
): Promise<{ id: string; status: string; submittedAt: string | null; score: number | null; feedback: string | null }> {
  const res = await fetch(`${apiBase}/lms/me/homework/${encodeURIComponent(assignmentId)}/submit`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to submit homework');
  return data as { id: string; status: string; submittedAt: string | null; score: number | null; feedback: string | null };
}

// --- LMS: payment records (for receipts) ---

/** Payment record (completed PayHero payment) for dashboard receipts. */
export interface LmsPaymentRecord {
  id: string;
  courseId: string;
  courseTitle: string;
  amountKes: number;
  paidAt: string;
  reference?: string;
}

export async function lmsGetMyPaymentRecords(): Promise<{ records: LmsPaymentRecord[] }> {
  const res = await fetch(`${apiBase}/lms/me/payment-records`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load payment records');
  return data as { records: LmsPaymentRecord[] };
}

/** Download receipt PDF for a payment record. Returns blob for saving as file. */
export async function lmsDownloadPaymentReceipt(recordId: string): Promise<Blob> {
  const res = await fetch(`${apiBase}/lms/me/payment-records/${encodeURIComponent(recordId)}/receipt`, {
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message ?? data.error ?? 'Failed to download receipt');
  }
  return res.blob();
}

// --- Tutor: homework management ---

export interface TutorHomeworkAssignment {
  id: string;
  courseId: string;
  courseTitle: string;
  title: string;
  type: string;
  dueDate: string | null;
  maxScore: number;
  xpReward: number;
  instructions?: string;
  submissionsTotal: number;
  submissionsGraded: number;
  submissionsPending: number;
}

export interface TutorHomeworkSubmission {
  id: string;
  learnerId: string;
  learnerName: string;
  learnerEmail: string;
  status: string;
  submittedAt: string | null;
  score: number | null;
  feedback: string | null;
  content: string;
  fileNames: string[];
}

export async function getTutorHomeworkAssignments(): Promise<{ assignments: TutorHomeworkAssignment[] }> {
  const res = await fetch(`${apiBase}/tutor/homework/assignments`, { credentials: 'include' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load assignments');
  return data as { assignments: TutorHomeworkAssignment[] };
}

export async function getTutorHomeworkSubmissions(assignmentId: string): Promise<{ submissions: TutorHomeworkSubmission[] }> {
  const res = await fetch(
    `${apiBase}/tutor/homework/assignments/${encodeURIComponent(assignmentId)}/submissions`,
    { credentials: 'include' }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to load submissions');
  return data as { submissions: TutorHomeworkSubmission[] };
}

export async function createTutorHomeworkAssignment(payload: {
  courseId: string;
  sectionId?: string;
  title: string;
  description?: string;
  type?: string;
  dueDate?: string;
  maxScore?: number;
  xpReward?: number;
  instructions?: string;
}): Promise<TutorHomeworkAssignment> {
  const res = await fetch(`${apiBase}/tutor/homework/assignments`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to create assignment');
  return data as TutorHomeworkAssignment;
}

export async function updateTutorHomeworkAssignment(
  assignmentId: string,
  payload: {
    title?: string;
    dueDate?: string | null;
    maxScore?: number;
    xpReward?: number;
    instructions?: string;
  }
): Promise<Pick<TutorHomeworkAssignment, 'id' | 'courseId' | 'title' | 'type' | 'dueDate' | 'maxScore' | 'xpReward'>> {
  const res = await fetch(`${apiBase}/tutor/homework/assignments/${encodeURIComponent(assignmentId)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to update assignment');
  return data as Pick<
    TutorHomeworkAssignment,
    'id' | 'courseId' | 'title' | 'type' | 'dueDate' | 'maxScore' | 'xpReward'
  >;
}

export async function gradeTutorHomeworkSubmission(
  submissionId: string,
  payload: { score: number; feedback?: string }
): Promise<{ ok: boolean }> {
  const res = await fetch(`${apiBase}/tutor/homework/submissions/${encodeURIComponent(submissionId)}/grade`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message ?? data.error ?? 'Failed to grade submission');
  return data as { ok: boolean };
}

export async function deleteTutorHomeworkAssignment(assignmentId: string): Promise<void> {
  const res = await fetch(`${apiBase}/tutor/homework/assignments/${encodeURIComponent(assignmentId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message ?? data.error ?? 'Failed to delete assignment');
  }
}
